import type { QueryClient } from '@tanstack/react-query';
import { createCacheKey, createDummyCart } from 'hooks/cart/useDummyCart';
import { PRODUCTS_UPDATED_PRICES_KEY } from 'hooks/product/useProductsUpdatedPrices';
import { fetchPosts, POSTS_KEY } from 'hooks/usePosts';
import { DEFAULT_COUNTRY_CODE } from 'lib/constants/siteDefaults';
import type { AllBlocks } from 'types/cms/PageModule';
import { getProductSellingPlans } from 'utils/product/getBlockSellingPlans';

/* For use in static props for any page that uses the PageModuleResolver. */
export const blocksBuildTimeResolver = async (
  queryClient: QueryClient,
  blocks?: AllBlocks,
) => {
  if (!blocks) return;

  for (const block of blocks) {
    switch (block._type) {
      case 'blogCarousel': {
        await queryClient.prefetchQuery({
          queryKey: [POSTS_KEY, 5, null, null],
          queryFn: fetchPosts,
        });
        break;
      }
      case 'timeline_productBlock': {
        if (!block.promoCodes || !block.promoCodes.length) {
          /* INFO: No need to make dummy cart without promocode */
          break;
        }

        const selectorPlans = await getProductSellingPlans(
          queryClient,
          block.productWithVariants.product.slug,
        );

        if (selectorPlans) {
          await queryClient.prefetchQuery({
            queryKey: [
              PRODUCTS_UPDATED_PRICES_KEY,
              block._key,
              selectorPlans.id,
              DEFAULT_COUNTRY_CODE,
              createCacheKey(selectorPlans.lines, block.promoCodes),
            ],
            queryFn: () =>
              createDummyCart({
                lines: selectorPlans.lines,
                promoCodes: block.promoCodes,
                countryCode: DEFAULT_COUNTRY_CODE,
              }),
          });
        }
        break;
      }
      case 'timeline_supplementPicker': {
        if (!block.promoCodes || !block.promoCodes.length) {
          /* INFO: No need to make dummy cart without promocode */
          break;
        }

        /* INFO: The runtime hook builds one dummy cart per product, so warm a
         * cart for each product (not a single combined cart). */
        for (const { product } of block.products) {
          const productPlans = await getProductSellingPlans(
            queryClient,
            product.slug,
          );

          if (productPlans) {
            await queryClient.prefetchQuery({
              queryKey: [
                PRODUCTS_UPDATED_PRICES_KEY,
                block._key,
                productPlans.id,
                DEFAULT_COUNTRY_CODE,
                createCacheKey(productPlans.lines, block.promoCodes),
              ],
              queryFn: () =>
                createDummyCart({
                  lines: productPlans.lines,
                  promoCodes: block.promoCodes,
                  countryCode: DEFAULT_COUNTRY_CODE,
                }),
            });
          }
        }
        break;
      }
      case 'timeline_skincareFormatPicker': {
        if (!block.promoCodes || !block.promoCodes.length) {
          break;
        }

        for (const { product } of block.productFormats) {
          const productPlans = await getProductSellingPlans(
            queryClient,
            product.slug,
          );

          if (productPlans) {
            await queryClient.prefetchQuery({
              queryKey: [
                PRODUCTS_UPDATED_PRICES_KEY,
                block._key,
                productPlans.id,
                DEFAULT_COUNTRY_CODE,
                createCacheKey(productPlans.lines, block.promoCodes),
              ],
              queryFn: () =>
                createDummyCart({
                  lines: productPlans.lines,
                  promoCodes: block.promoCodes,
                  countryCode: DEFAULT_COUNTRY_CODE,
                }),
            });
          }
        }
        break;
      }
      default:
        break;
    }
  }
};
